import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { notificationsApi, PLACEHOLDER_IMAGE } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { cn, formatDate } from '../../lib/utils';

export const NotificationPopup = () => {
    const { user } = useAuth();
    const [visibleNotifications, setVisibleNotifications] = useState([]);
    const [seenIds, setSeenIds] = useState(new Set());

    const dismissNotification = useCallback(async (notificationId) => {
        setVisibleNotifications(prev => prev.filter(n => n.id !== notificationId));
        setSeenIds(prev => new Set([...prev, notificationId]));
        
        if (user?.discord_id) {
            try {
                await notificationsApi.dismiss(notificationId, user.discord_id);
            } catch (error) {
                console.error('Failed to dismiss notification:', error);
            }
        }
    }, [user?.discord_id]);

    const fetchNotifications = useCallback(async () => {
        if (!user?.discord_id) return;
        try {
            const response = await notificationsApi.getAll(user.discord_id, 10);
            const notifications = response.data || [];
            
            // Filter notifications already seen/dismissed or already visible
            const newNotifications = notifications.filter(n => {
                if (n.is_read) return false;
                if (seenIds.has(n.id)) return false;
                if (visibleNotifications.find(v => v.id === n.id)) return false;
                return true;
            });

            if (newNotifications.length > 0) {
                // Add new notifications (max 3 at a time)
                const toAdd = newNotifications.slice(0, 3);
                setVisibleNotifications(prev => [...prev, ...toAdd]);
                
                // Auto-dismiss after 5 seconds
                toAdd.forEach(notif => {
                    setTimeout(() => {
                        dismissNotification(notif.id);
                    }, 5000);
                });
            }
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    }, [user?.discord_id, seenIds, visibleNotifications, dismissNotification]);

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 10000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    if (visibleNotifications.length === 0) return null;

    return (
        <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-3 max-w-md">
            {visibleNotifications.map((notification, index) => (
                <div
                    key={notification.id}
                    data-testid={`notification-popup-${notification.id}`}
                    className={cn(
                        "flex bg-[#0A0A0A]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden",
                        "animate-slide-in-right"
                    )}
                    style={{ animationDelay: `${index * 100}ms` }}
                >
                    {/* Left: Image */}
                    <div className="w-20 h-20 shrink-0 bg-black/50">
                        <img
                            src={notification.image_url || PLACEHOLDER_IMAGE}
                            alt=""
                            className="w-full h-full object-cover"
                        />
                    </div>

                    {/* Middle: Content */}
                    <div className="flex-1 p-3 min-w-0 relative">
                        {/* META Tag - Top Right of content area */}
                        {notification.is_meta && (
                            <div className="absolute top-2 right-2">
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-orange-500 text-black tracking-wider">
                                    META
                                </span>
                            </div>
                        )}

                        {/* Header: Item Name */}
                        <h4 className="font-semibold text-white text-sm truncate pr-12">
                            {notification.title}
                        </h4>

                        {/* Footer: Creator & Date */}
                        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                            <span className="truncate">
                                von <span className="text-cyan-400">{notification.creator_name || 'Admin'}</span>
                            </span>
                            <span>•</span>
                            <span className="shrink-0">
                                {formatDate(notification.created_at)}
                            </span>
                        </div>
                    </div>

                    {/* Right: Close Button - Separate section */}
                    <div className="w-10 shrink-0 border-l border-white/10 flex items-center justify-center bg-white/5 hover:bg-white/10 transition-colors">
                        <button
                            onClick={() => dismissNotification(notification.id)}
                            className="w-full h-full flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                            data-testid={`dismiss-notification-${notification.id}`}
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
};
